// src/store/reducers/paymentReducer.ts

import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { fetchApi } from '../../utilities/api';
import { ENDPOINTS } from '../../utilities/endpoints';


interface RawCard {
  paymentMethodId: string;
  cardScheme: string;
  expiryMonth: string;
  expiryYear: string;
  maskedCard: string;
  nameOnCard: string;
  stripeCustomerId: string;
  isDefault: boolean;
}

export interface PaymentMethod {
  id: string;
  type: string;
  last4: string;
  expMonth: number;
  expYear: number;
  name: string;
  isDefault: boolean;
}


interface BuyArgs {
  propertyId:     string;
  paymentMethodId:string;
  bidAmount:      number;
  name:           string;
  lat:            number;
  lng:            number;
}

interface RawPayoutCard {
  id: string;
  object: string;
  default_for_currency: boolean;
  brand: string;        
  last4: string;        
  exp_month: number;    
  exp_year: number;     
}

export interface PayoutCard {
  id: string;
  brand: string;
  last4: string;
  expMonth: number;
  expYear: number;
  isDefault: boolean;
    account: string; // Add this line if 'account' is expected from backend

}
export interface ExternalAccountResponse {
  isStripeIntegrated:     boolean;
  areBankDetailsSubmitted:boolean;
  last4:                  string;
}

export const buyProperty = createAsyncThunk<
  { message: string },
  BuyArgs,
  { rejectValue: string }
>(
  'payment/buyProperty',
  async (
    { propertyId, paymentMethodId, bidAmount, name, lat, lng },
    { rejectWithValue }
  ) => {
    const token = localStorage.getItem('token');
    if (!token) return rejectWithValue('No auth token found');

    const payload = {
      buildingId:      propertyId,
      paymentMethodId,
      bidAmount,
      name,
      isApplePay:      false,
      location: { lat, lng }
    };


    try {
      const response = await fetchApi<{ message: string }>(
        ENDPOINTS.PROPERTIES.BUY,
        {
          method: 'POST',
          headers: {
            Authorization: token,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        }
      );

      return response.data;
    } catch (err: any) {
      console.error('[buyProperty] error:', err.response?.data || err);
      return rejectWithValue(
        err.response?.data?.data?.message ||
        err.message ||
        'Failed to buy property'
      );
    }
  }
);


export const fetchPaymentMethods = createAsyncThunk<
  PaymentMethod[],
  void,
  { rejectValue: string }
>(
  'payment/fetchPaymentMethods',
  async (_, { rejectWithValue }) => {
    const token = localStorage.getItem('token');
    if (!token) {
      return rejectWithValue('No auth token found');
    }

    try {
      const response = await fetchApi<{ cardList: RawCard[] }>(
        ENDPOINTS.USERS.GET_PAYMENT_METHOD,
        {
          method: 'GET',
          headers: { Authorization: `${token}` },
        }
      );

      return response.data.cardList.map((c) => ({
        id:       c.paymentMethodId,
        type:     c.cardScheme,
        last4:    c.maskedCard.slice(-4),
        expMonth: Number(c.expiryMonth),
        expYear:  Number(c.expiryYear),
        name:     c.nameOnCard,
        isDefault:c.isDefault,
      }));
    } catch (err: any) {
      return rejectWithValue(err.message || 'Failed to load payment methods');
    }
  }
);

export const deletePaymentMethod = createAsyncThunk<
  string,          // return the deleted paymentMethodId
  string,          // arg is the paymentMethodId to delete
  { rejectValue: string }
>(
  'payment/deletePaymentMethod',
  async (id, { rejectWithValue }) => {
    const token = localStorage.getItem('token');
    if (!token) return rejectWithValue('No auth token found');

    // **CALL** the function with your `id`
    const url = ENDPOINTS.USERS.DELETE_PAYMENT(id);
    try {
      await fetchApi<void>(url, {
        method: 'DELETE',
        headers: { Authorization: token },
      });
      return id;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.message ||
        err.message ||
        'Failed to delete payment method'
      );
    }
  }
);

export const setDefaultPaymentMethod = createAsyncThunk<
  string,               // returns the new default paymentMethodId
  string,               // accepts the paymentMethodId to set as default
  { rejectValue: string }
>(
  'payment/setDefaultPaymentMethod',
  async (paymentMethodId, { rejectWithValue }) => {
    const token = localStorage.getItem('token');
    if (!token) return rejectWithValue('No auth token found');

    const url = ENDPOINTS.USERS.SET_DEFAULT_PAYMENT_METHOD;
    try {
      // POST { paymentMethodId } 
      const response = await fetchApi<{ message: string }>(
        url,
        {
          method: 'POST',
          headers: {
            Authorization: token,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ paymentMethodId }),
        }
      );
      return paymentMethodId;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.message ||
        err.message ||
        'Failed to set default payment method'
      );
    }
  }
);


export const fetchPayoutCards = createAsyncThunk<PayoutCard[], void, { rejectValue: string }>(
  'payment/fetchPayoutCards',
  async (_, { rejectWithValue }) => {
    const token = localStorage.getItem('token');
    if (!token) return rejectWithValue('No auth token found');

    try {
      const response = await fetchApi<{ response: RawPayoutCard[] }>(
        ENDPOINTS.USERS.EXTERNAL_ACCOUNT_LIST,
        {
          method: 'GET',
          headers: { Authorization: token },
        }
      );
      return response.data.response.map(card => ({
        id:        card.id,
        brand:     card.brand,                 
        last4:     card.last4,
        expMonth:  card.exp_month,
        expYear:   card.exp_year,
        isDefault: card.default_for_currency,
        account:   (card as any).account ?? '', // fallback to empty string if not present
      }));
    } catch (err: any) {
      return rejectWithValue(err.message || 'Failed to load payout cards');
    }
  }
);

export const setDefaultPayoutCard = createAsyncThunk<
  string,           
  string,         
  { rejectValue: string }
>(
  'payment/setDefaultPayoutCard',
  async (cardId, { rejectWithValue }) => {
    const token = localStorage.getItem('token');
    if (!token) return rejectWithValue('No auth token found');

    try {
      await fetchApi<{ message: string }>(
        ENDPOINTS.USERS.SET_DEFAULT_BANK_ACCOUNT,
        {
          method: 'PUT',
          headers: {
            Authorization: token,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ AccountId: cardId, defaultCard: true }),
        }
      );
      return cardId;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || err.message || 'Failed to set default payout card');
    }
  }
);


export const deletePayoutCard = createAsyncThunk<
  string,              
  string,              
  { rejectValue: string }
>(
  'payment/deletePayoutCard',
  async (id, { rejectWithValue }) => {
    const token = localStorage.getItem('token');
    if (!token) return rejectWithValue('No auth token found');

    try {
      await fetchApi<void>(
        ENDPOINTS.USERS.DELETE_BANK_ACCOUNT(id),
        {
          method: 'DELETE',
          headers: { Authorization: token },
        }
      );
      return id;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.message ||
        err.message ||
        'Failed to delete payout account'
      );
    }
  }
);

interface PaymentState {
  cardList:      PaymentMethod[];
  isFetching:    boolean;
  fetchError:    string | null;

  // new fields for buyProperty
  isBuying:      boolean;
  buyError:      string | null;
  buySuccessMsg: string | null;
  isDeleting:    boolean;
  deleteError:   string | null;
  isSettingDefault: boolean;
  setDefaultError:  string | null;

 payoutCards:          PayoutCard[];
  isFetchingPayout:     boolean;
  fetchPayoutError:     string | null;
  isAddingPayout:       boolean;
  addPayoutError:       string | null;
  isDeletingPayout:     boolean;
  deletePayoutError:    string | null;
  isSettingDefaultPayout: boolean;
  setDefaultPayoutError:  string | null;

    isAddingPayoutV1: boolean;
  addPayoutV1Error: string | null;


}

const initialState: PaymentState = {
  cardList:      [],
  isFetching:    false,
  fetchError:    null,

  isBuying:      false,
  buyError:      null,
  buySuccessMsg: null,
  isDeleting:    false,
  deleteError:   null,
  isSettingDefault:  false,
  setDefaultError:   null,

 payoutCards:            [],
  isFetchingPayout:       false,
  fetchPayoutError:       null,
  isAddingPayout:         false,
  addPayoutError:         null,
  isDeletingPayout:       false,
  deletePayoutError:      null,
  isSettingDefaultPayout: false,
  setDefaultPayoutError:  null,

    isAddingPayoutV1: false,
  addPayoutV1Error: null,
};

const paymentSlice = createSlice({
  name: 'payment',
  initialState,
  reducers: {
    clearCards(state) {
      state.cardList   = [];
      state.fetchError = null;
    },
    clearBuyState(state) {
      state.isBuying      = false;
      state.buyError      = null;
      state.buySuccessMsg = null;
    }
  },
  extraReducers: (builder) => {
    // fetchPaymentMethods
    builder
      .addCase(fetchPaymentMethods.pending, (state) => {
        state.isFetching = true;
        state.fetchError = null;
      })
      .addCase(fetchPaymentMethods.fulfilled, (state, action: PayloadAction<PaymentMethod[]>) => {
        state.isFetching = false;
        state.cardList   = action.payload;
      })
      .addCase(fetchPaymentMethods.rejected, (state, action) => {
        state.isFetching = false;
        state.fetchError = action.payload ?? action.error.message ?? 'Unknown error';
      });

    // buyProperty
    builder
      .addCase(buyProperty.pending, (state) => {
        state.isBuying      = true;
        state.buyError      = null;
        state.buySuccessMsg = null;
      })
      .addCase(buyProperty.fulfilled, (state, action: PayloadAction<{ message: string }>) => {
        state.isBuying      = false;
        state.buySuccessMsg = action.payload.message;
      })
      .addCase(buyProperty.rejected, (state, action) => {
        state.isBuying = false;
        state.buyError = action.payload ?? action.error.message ?? 'Purchase failed';
      });

      builder
      .addCase(deletePaymentMethod.pending, (state) => {
        state.isDeleting  = true;
        state.deleteError = null;
      })
      .addCase(deletePaymentMethod.fulfilled, (state, action: PayloadAction<string>) => {
        state.isDeleting  = false;
        // remove the deleted card from the list
        state.cardList    = state.cardList.filter(c => c.id !== action.payload);
      })
      .addCase(deletePaymentMethod.rejected, (state, action) => {
        state.isDeleting  = false;
        state.deleteError = action.payload ?? action.error.message ?? 'Unable to delete card';
      });

      builder
      .addCase(setDefaultPaymentMethod.pending, (state) => {
        state.isSettingDefault = true;
        state.setDefaultError  = null;
      })
      .addCase(
        setDefaultPaymentMethod.fulfilled,
        (state, action: PayloadAction<string>) => {
          state.isSettingDefault = false;
          // flip isDefault flags
          state.cardList = state.cardList.map((c) => ({
            ...c,
            isDefault: c.id === action.payload,
          }));
        }
      )
      .addCase(setDefaultPaymentMethod.rejected, (state, action) => {
        state.isSettingDefault = false;
        state.setDefaultError  =
          action.payload ?? action.error.message ?? 'Could not set default';
      });
          builder
      .addCase(fetchPayoutCards.pending, state => {
        state.isFetchingPayout   = true;
        state.fetchPayoutError   = null;
      })
      .addCase(fetchPayoutCards.fulfilled, (state, action: PayloadAction<PayoutCard[]>) => {
        state.isFetchingPayout   = false;
        state.payoutCards         = action.payload;
      })
      .addCase(fetchPayoutCards.rejected, (state, action) => {
        state.isFetchingPayout   = false;
        state.fetchPayoutError   = action.payload ?? action.error.message ?? 'Unknown error';
      });

    builder
      .addCase(setDefaultPayoutCard.pending, state => {
        state.isSettingDefaultPayout = true;
        state.setDefaultPayoutError  = null;
      })
      .addCase(setDefaultPayoutCard.fulfilled, (state, action: PayloadAction<string>) => {
        state.isSettingDefaultPayout = false;
        state.payoutCards = state.payoutCards.map(card => ({
          ...card,
          isDefault: card.id === action.payload,
        }));
      })
      .addCase(setDefaultPayoutCard.rejected, (state, action) => {
        state.isSettingDefaultPayout = false;
        state.setDefaultPayoutError  =
          action.payload ?? action.error.message ?? 'Could not set default';
      });

      builder

  .addCase(deletePayoutCard.pending, state => {
    state.isDeletingPayout  = true;
    state.deletePayoutError = null;
  })
  .addCase(deletePayoutCard.fulfilled, (state, action: PayloadAction<string>) => {
    state.isDeletingPayout  = false;
    state.payoutCards       = state.payoutCards.filter(pc => pc.id !== action.payload);
  })
  .addCase(deletePayoutCard.rejected, (state, action) => {
    state.isDeletingPayout  = false;
    state.deletePayoutError = action.payload ?? action.error.message ?? 'Could not delete payout account';
  });

  },
});

export const { clearCards, clearBuyState } = paymentSlice.actions;
export default paymentSlice.reducer;
